/* prettier-ignore-file */
/**
 * Running order summary, pinned to the bottom of each wizard step.
 *
 * Replaces the single total that used to sit in the header. A bare number answers
 * "how much" but not "for what" — as the customer moves through the steps this shows
 * the booking accumulating, so the total is always attributable to something.
 *
 * Sections appear only once they have content, so step 1 shows just the dates rather
 * than a wall of empty rows.
 *
 * Not rendered on the review step, which already carries a full itemised breakdown.
 */

import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { formatCentsAsUSD } from "../../utils/formatting";

const shortDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

const WizardSummary = ({
  draft,
  quote,
  products2,
  product1Title,
  product2Title,
  useDurationDiscounts,
  durationDiscounts,
}) => {
  /**
   * The cheapest tier that beats what's currently applied, and how many more days it
   * needs. Null when discounts are off, none exist, or the customer already has the
   * best rate. Uses the same fromDays/toDays comparison as calculateDurationDiscountUtil.
   */
  const nextTier = React.useMemo(() => {
    if (!useDurationDiscounts || !(durationDiscounts || []).length) return null;
    if (!quote.days) return null;
    // A negotiated rate overrides tiers entirely, so promoting one would be a lie.
    if (quote.dailyRateOverrideCents != null) return null;

    const better = (durationDiscounts || [])
      .filter((d) => d.fromDays > quote.days && (d.value || 0) > (quote.durationDiscountPercent || 0))
      .sort((a, b) => a.fromDays - b.fromDays)[0];

    if (!better) return null;
    return { daysAway: better.fromDays - quote.days, value: better.value };
  }, [useDurationDiscounts, durationDiscounts, quote.days, quote.durationDiscountPercent, quote.dailyRateOverrideCents]);

  const bikes = Object.entries(draft.selectedBikes || {}).filter(([, q]) => q > 0);
  const addOns = Object.entries(draft.selectedAddOns || {}).filter(
    ([, v]) => v === true || v?.quantity > 0
  );

  const hasDates = draft.startDate && draft.endDate;
  const hasTransport =
    draft.deliveryNeeded || draft.homePickupNeeded || draft.inStorePickup;

  // Before anything is chosen there is nothing worth showing.
  if (!hasDates && bikes.length === 0 && addOns.length === 0) return null;

  const transportLabel = [
    draft.deliveryNeeded && "Delivery",
    draft.homePickupNeeded && "Home pickup",
    !draft.deliveryNeeded && "Shop pickup",
    !draft.homePickupNeeded && "Shop drop-off",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <View style={styles.container}>
      <Text style={styles.heading}>Your booking so far</Text>

      {hasDates && (
        <View style={styles.row}>
          <Text style={styles.label}>Dates</Text>
          <Text style={styles.value}>
            {shortDate(draft.startDate)} – {shortDate(draft.endDate)} ({quote.days}{" "}
            {quote.days === 1 ? "day" : "days"})
          </Text>
        </View>
      )}

      {bikes.length > 0 && (
        <View style={styles.group}>
          <Text style={styles.groupLabel}>{product1Title}</Text>
          {bikes.map(([name, quantity]) => (
            <Text key={name} style={styles.item}>
              {quantity}× {name}
            </Text>
          ))}
        </View>
      )}

      {addOns.length > 0 && (
        <View style={styles.group}>
          <Text style={styles.groupLabel}>{product2Title}</Text>
          {addOns.map(([id, value]) => {
            const product = (products2 || []).find((p) => p.id === id);
            const quantity = value === true ? 1 : value?.quantity || 0;
            return (
              <Text key={id} style={styles.item}>
                {quantity}× {product?.name || id}
              </Text>
            );
          })}
        </View>
      )}

      {hasTransport && (bikes.length > 0 || addOns.length > 0) && (
        <View style={styles.row}>
          <Text style={styles.label}>Transport</Text>
          <Text style={styles.value}>{transportLabel}</Text>
        </View>
      )}

      {draft.miles != null && (
        <View style={styles.row}>
          <Text style={styles.label}>Distance</Text>
          <Text style={styles.value}>{draft.miles} mi each way</Text>
        </View>
      )}

      {quote.dailyRateOverrideCents != null && (
        <View style={styles.row}>
          <Text style={styles.label}>Rate override</Text>
          <Text style={[styles.value, styles.override]}>
            {formatCentsAsUSD(quote.dailyRateOverrideCents)} / day each
          </Text>
        </View>
      )}

      {quote.finalTotalCents > 0 && (
        <>
          <View style={styles.divider} />

          {/* Discounts are itemised here, not just folded into the total. They are
              recomputed on every change, so as soon as the range crosses a tier the
              saving appears — otherwise the total simply drops and looks like a bug. */}
          {quote.durationDiscountCents > 0 && (
            <View style={styles.row}>
              <Text style={styles.discountLabel}>
                {quote.durationDiscountName || `Duration discount (${quote.durationDiscountPercent}%)`}
              </Text>
              <Text style={styles.discountValue}>
                −{formatCentsAsUSD(quote.durationDiscountCents)}
              </Text>
            </View>
          )}

          {quote.transportationDiscountCents > 0 && (
            <View style={styles.row}>
              <Text style={styles.discountLabel}>
                {quote.transportationDiscountName ||
                  `Transportation discount (${quote.transportationDiscountPercent}%)`}
              </Text>
              <Text style={styles.discountValue}>
                −{formatCentsAsUSD(quote.transportationDiscountCents)}
              </Text>
            </View>
          )}

          {/* Upsell: how much longer until the next tier. Only shown when a better
              rate genuinely exists for a longer rental. */}
          {nextTier && (
            <Text style={styles.nextTier}>
              Rent {nextTier.daysAway} more {nextTier.daysAway === 1 ? "day" : "days"} for{" "}
              {nextTier.value}% off {product1Title.toLowerCase()}
            </Text>
          )}

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total so far</Text>
            <Text style={styles.totalValue}>{formatCentsAsUSD(quote.finalTotalCents)}</Text>
          </View>
          {/* Transportation is a large line item; saying so prevents the total
              looking wrong once the address is confirmed. */}
          {(draft.deliveryNeeded || draft.homePickupNeeded) && draft.miles == null && (
            <Text style={styles.pending}>Transportation not included yet</Text>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
  },
  heading: {
    fontSize: 11,
    fontWeight: "700",
    color: "#8A9794",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: 3,
  },
  label: { fontSize: 13, color: "#5C6B68", paddingRight: 10 },
  value: { fontSize: 13, color: "#2E3A38", fontWeight: "500", flexShrink: 1, textAlign: "right" },
  override: { color: "#7A4E12" },

  group: { paddingVertical: 4 },
  groupLabel: { fontSize: 11, color: "#8A9794", textTransform: "uppercase", marginBottom: 2 },
  item: { fontSize: 13, color: "#2E3A38", paddingVertical: 1 },

  divider: { height: 1, backgroundColor: "#EDF1F0", marginVertical: 9 },
  discountLabel: { fontSize: 13, color: "#1F8A5B", flexShrink: 1, paddingRight: 10 },
  discountValue: { fontSize: 13, color: "#1F8A5B", fontWeight: "700" },
  nextTier: { fontSize: 12, color: "#B4761B", marginTop: 6, marginBottom: 2 },
  totalRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  totalLabel: { fontSize: 15, fontWeight: "700", color: "#2E3A38" },
  totalValue: { fontSize: 19, fontWeight: "800", color: "#0E7C6B" },
  pending: { fontSize: 11, color: "#B4761B", marginTop: 6 },
});

export default WizardSummary;
